import { Bullets, Code, HelpSection, Note, P, Screen, SeeAlso, Terms } from '../prose';

/**
 * 工数がどう記録されるか。
 *
 * 「聞かれないこと」を先に説明する。カテゴリと資産化を利用者が決めるものだと
 * 誤解されると、会計上の区分が会話ごとにブレる。
 */
export function LoggingTimeArticle() {
  return (
    <>
      <HelpSection heading="頼み方">
        <P>
          {'特別な言い回しは要りません。作業の区切りで、かかった時間が分かるように頼むだけです。'}
        </P>
        <Bullets
          items={[
            '「今日の作業を INOU に記録して。3 時間くらい」',
            '「昨日のレビュー対応、1.5 時間で入れておいて」',
            '「今月あと何時間アサインが残ってる？」',
          ]}
        />
        <P>
          {
            '工数は 0.25 時間（15 分）単位に丸められます。日付を言わなければ、テナントのタイムゾーンでの当日として記録されます。「昨日の分」のように当日以外を記録するときは、日付が伝わるように頼んでください。'
          }
        </P>
      </HelpSection>

      <HelpSection heading="記録先はどう決まるか">
        <P>
          {
            '作業中のリポジトリがプロジェクトに紐付けてあれば、それが記録先になります。1 つのリポジトリは 1 つのプロジェクトにしか紐付かないので、迷いは起きません。'
          }
        </P>
        <P>
          {
            '紐付けが無い、あるいは複数の候補に当たる場合は、AI が一覧から選ぶか、あなたに尋ねます。紐付けの登録・変更ができるのは管理者だけです（会計上どこに付くかを決める操作のため）。'
          }
        </P>
        <Note label="表記ゆれ">
          <P>
            <Code>git@github.com:acme/api.git</Code>
            {' と '}
            <Code>https://github.com/acme/api</Code>
            {' は同じものとして扱われます。末尾の '}
            <Code>.git</Code>
            {' や大文字小文字の違いも吸収します。'}
          </P>
        </Note>
      </HelpSection>

      <HelpSection heading="聞かれないこと">
        <P>
          {
            '作業カテゴリと資産化の可否は、あなたに聞きません。会計上の区分であって、作業のたびに判断してもらうものではないからです。'
          }
        </P>
        <Terms
          rows={[
            {
              term: '作業カテゴリ',
              value: (
                <>
                  {'会話の内容から AI が決めます（'}
                  <Code>development</Code>
                  {' / '}
                  <Code>maintenance</Code>
                  {' / '}
                  <Code>meeting</Code>
                  {' / '}
                  <Code>other</Code>
                  {'）。判断できないときはサーバーがメモ本文から推測し、それでも分からなければ '}
                  <Code>development</Code>
                  {' になります。'}
                </>
              ),
            },
            {
              term: '資産化',
              value:
                'サーバーが導き出します。プロジェクトが資産化の対象で、かつカテゴリが development のときだけ資産化されます。指定はできません。',
            },
            {
              term: '請求可能かどうか',
              value:
                'プロジェクト種別の既定に従います。受託のプロジェクトなら請求可能として記録されます。',
            },
          ]}
        />
      </HelpSection>

      <HelpSection heading="直したいとき">
        <P>
          {
            '自分の記録なら、AI にそのまま頼めます。「さっきの 3 時間を 2 時間に直して」で訂正でき、削除も同じように頼めます。カテゴリを変えると、資産化の区分もサーバー側で計算し直されます。'
          }
        </P>
        <P>
          {'画面から直すなら '}
          <Screen to="/time-logs">工数明細</Screen>
          {' です。こちらは管理者が他の人の記録を訂正するための窓口でもあります。'}
        </P>
        <Note label="記録は本人だけ">
          <P>
            {
              '新しい記録を作れるのは、経路を問わず本人だけです。管理者でも他の人の分を代わりに記録することはできません（できてしまうと、1 人が全員分を入力する運用が成立してしまうため）。訂正だけが例外です。'
            }
          </P>
        </Note>
        <Bullets
          items={[
            <>
              {'記録した数字がレポートでどう数えられるかは '}
              <SeeAlso slug="reading-reports">レポートの読み方</SeeAlso>
              {' にあります。'}
            </>,
          ]}
        />
      </HelpSection>
    </>
  );
}
